import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// 42px is the empirically measured height of a single-line table row in both
// Report and TableSummaryReport (line-height + vertical padding + border).
const DEFAULT_ROW_HEIGHT = 42;

// Render 5 extra rows above and below the visible window so fast scrolling
// doesn't flash empty space before the next rAF paint.
const DEFAULT_BUFFER_ROWS = 5;

// Used on first render before the container ref is measured. 600px covers
// ~14 rows at 42px each, which is a reasonable viewport for most screens.
const DEFAULT_FALLBACK_HEIGHT = 600;

interface UseVirtualizedRowsOptions {
  rowHeight?: number;
  bufferRows?: number;
  fallbackHeight?: number;
}

/**
 * Lightweight row virtualization driven by a scroll container ref.
 *
 * Instead of rendering thousands of DOM nodes, this hook only renders the
 * rows visible within the scroll container plus a small buffer. The consumer
 * renders spacer divs with `topPadding` and `bottomPadding` heights to
 * preserve the total scrollable area, keeping the scrollbar proportional.
 *
 * Usage (example shows table-based spacers — adapt the element to your context):
 *   const { containerRef, visibleRows, topPadding, bottomPadding, handleScroll }
 *     = useVirtualizedRows(allRows);
 *
 *   <div ref={containerRef} onScroll={handleScroll} style={{ overflow: 'auto' }}>
 *     <table>
 *       <tbody>
 *         <tr><td colSpan={cols} style={{ height: topPadding }} /></tr>
 *         {visibleRows.map(row => <tr>...</tr>)}
 *         <tr><td colSpan={cols} style={{ height: bottomPadding }} /></tr>
 *       </tbody>
 *     </table>
 *   </div>
 */
export function useVirtualizedRows<T>(rows: T[], options: UseVirtualizedRowsOptions = {}) {
  const {
    rowHeight = DEFAULT_ROW_HEIGHT,
    bufferRows = DEFAULT_BUFFER_ROWS,
    fallbackHeight = DEFAULT_FALLBACK_HEIGHT,
  } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const rafRef = useRef<number>();

  // Debounce scroll events to one update per animation frame. Without this,
  // rapid scroll events would cause dozens of re-renders per second.
  const handleScroll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (containerRef.current) {
        setScrollTop(containerRef.current.scrollTop);
      }
    });
  }, []);

  // Cancel any pending rAF on unmount to avoid setting state after teardown.
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Container height determines how many rows the useMemo slice renders.
  // Two mechanisms keep it in sync:
  //
  //   1. useLayoutEffect (below) — runs on mount and whenever the row count
  //      changes (filters, tab switches, new data). It reads clientHeight
  //      directly and also re-syncs the scroll offset from the DOM.
  //
  //   2. ResizeObserver — catches container size changes that happen *between*
  //      row-count changes, e.g. the user resizing the browser window while
  //      the row count stays the same. Without this, containerHeight would go
  //      stale and the hook would render too few or too many rows.
  //
  // The > 0 guard on both paths prevents overwriting the fallback when the
  // container hasn't been laid out yet (clientHeight is 0 on initial mount
  // or when the tab pane is hidden).
  const [containerHeight, setContainerHeight] = useState(fallbackHeight);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Re-sync scroll offset from the DOM. The container may have been scrolled
    // while hidden (e.g. tab switch with unmountOnExit: false) and no scroll
    // event fires on re-show, leaving the hook's scrollTop stale.
    setScrollTop(el.scrollTop);

    const measured = el.clientHeight;
    if (measured > 0) setContainerHeight(measured);

    // Attach a ResizeObserver to track container size changes between
    // row-count changes. Feature-gated for environments without support
    // (the clientHeight measurement above is the fallback).
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const h = entry.contentRect.height;
      if (h > 0) setContainerHeight(h);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [rows.length]);

  // Compute the visible slice and spacer heights. Recomputes when the source
  // rows change, the user scrolls, or the container resizes (via re-render).
  const { visibleRows, topPadding, bottomPadding } = useMemo(() => {
    const totalRows = rows.length;
    if (totalRows === 0) return { visibleRows: [] as T[], topPadding: 0, bottomPadding: 0 };

    // Expand the window by bufferRows in each direction to prevent flicker.
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - bufferRows);
    const endIndex = Math.min(
      totalRows,
      Math.ceil((scrollTop + containerHeight) / rowHeight) + bufferRows,
    );

    return {
      visibleRows: rows.slice(startIndex, endIndex),
      // Spacer heights keep the scrollbar thumb proportional to total content.
      topPadding: startIndex * rowHeight,
      bottomPadding: Math.max(0, (totalRows - endIndex) * rowHeight),
    };
  }, [rows, scrollTop, containerHeight, rowHeight, bufferRows]);

  return { containerRef, visibleRows, topPadding, bottomPadding, handleScroll };
}
